import { Component, effect, Input, OnInit } from '@angular/core';
import { MinimapIconComponent } from '../common/icons/minimap-icon/minimap-icon.component';
import { CosmeticOptions, Filter, TierCosmetic } from '../models/filter-models';
import { Color, MinimapIconShape, MinimapIconSize } from "../models/enums";
import { BaseTypeTier } from "../models/enums";
import { GearTierIconComponent } from '../common/icons/gear-tier-icon/gear-tier-icon.component';
import { BaseComponent } from '../common/base.component';
import { FilterService } from '../services/filter.service';

@Component({
  selector: 'app-legend',
  imports: [MinimapIconComponent, GearTierIconComponent],
  templateUrl: './legend.component.html',
  styleUrl: './legend.component.scss'
})
export class LegendComponent extends BaseComponent {
  @Input({ required: true }) filter!: Filter;

  sTierColor?: string;
  aTierColor?: string;
  bTierColor?: string;
  cTierColor?: string;
  dTierColor?: string;

  minimapIconCurrencyCosmetics?: CosmeticOptions[];
  minimapIconItemsCosmetics?: CosmeticOptions[];
  minimapIconWaystonesCosmetics?: CosmeticOptions[];
  minimapIconGemCosmetics?: CosmeticOptions[];
  minimapIconGoldCosmetics?: CosmeticOptions[];

  BaseTypeTier = BaseTypeTier;

  initialFilterUpdatedCounter = 0;

  constructor(private filterService: FilterService) {
    super();
    effect(() => {
      const currentCounter = this.filterService.dirtyFilterUpdated();
      if (currentCounter !== this.initialFilterUpdatedCounter) {
        this.initializeCosmetics();
      }
    });
  }

  initializeCosmetics(): void {
    this.sTierColor = this.getTierColor('S');
    this.aTierColor = this.getTierColor('A');
    this.bTierColor = this.getTierColor('B');
    this.cTierColor = this.getTierColor('C');
    this.dTierColor = this.getTierColor('D');

    // Collect unique minimap icons from currency tiers (ordered from S-tier to lower)
    const currencyIcons = this.getUniqueMinimapIcons(this.filter.tierCosmetics, MinimapIconShape.Circle);

    // Collect unique minimap icons from equipment tiers
    let equipmentIcons = this.getUniqueMinimapIcons(this.filter.equipmentTierCosmetics, MinimapIconShape.Kite);
    if (this.filter.equipmentCosmeticsSameAsTierCosmetics && this.filter.equipmentCosmeticsSameAsTierCosmeticsOverrides.displayOptions?.active) {
      equipmentIcons = this.getUniqueMinimapIcons(this.filter.tierCosmetics, MinimapIconShape.Kite);
    }

    // Handle style overrides - if active, use override; otherwise use equipment icons
    let itemsIcons = equipmentIcons;
    let mapIcons = equipmentIcons;
    let gemIcons = equipmentIcons;
    let goldIcons = equipmentIcons;

    if (this.filter.mapStyleOverride?.displayOptions?.active) {
      const overrideIcon = this.filter.mapStyleOverride?.displayOptions?.cosmeticOptions?.minimapIconShape;
      mapIcons = overrideIcon ? [new CosmeticOptions({ highlight: true, highlightColor: Color.White, minimapIconSize: MinimapIconSize.Small, minimapIconShape: overrideIcon })] : equipmentIcons;
    }
    if (this.filter.gemStyleOverride?.displayOptions?.active) {
      const overrideIcon = this.filter.gemStyleOverride?.displayOptions?.cosmeticOptions?.minimapIconShape;
      gemIcons = overrideIcon ? [new CosmeticOptions({ highlight: true, highlightColor: Color.White, minimapIconSize: MinimapIconSize.Small, minimapIconShape: overrideIcon })] : equipmentIcons;
    }
    if (this.filter.goldStyleOverride?.displayOptions?.active) {
      const overrideIcon = this.filter.goldStyleOverride?.displayOptions?.cosmeticOptions?.minimapIconShape;
      goldIcons = overrideIcon ? [new CosmeticOptions({ highlight: true, highlightColor: Color.White, minimapIconSize: MinimapIconSize.Small, minimapIconShape: overrideIcon })] : equipmentIcons;
    }

    this.minimapIconCurrencyCosmetics = currencyIcons;
    this.minimapIconItemsCosmetics = itemsIcons;
    this.minimapIconWaystonesCosmetics = mapIcons;
    this.minimapIconGemCosmetics = gemIcons;
    this.minimapIconGoldCosmetics = goldIcons;

    // if (this.filter.cosmeticSettings.highlightMinimapIconShapeItems) {
    //   this.minimapIconItemsCosmetics = new CosmeticOptions({ highlight: true, highlightColor: Color.White, minimapIconSize: MinimapIconSize.Small, minimapIconShape: this.filter.cosmeticSettings.highlightMinimapIconShapeItems });
    //   this.minimapIconWaystonesCosmetics = new CosmeticOptions({ highlight: true, highlightColor: Color.White, minimapIconSize: MinimapIconSize.Small, minimapIconShape: this.filter.cosmeticSettings.highlightMinimapIconShapeWaystones });
    // } else {
    // }
  }

  /**
   * Collects unique minimap icons from tier cosmetics, ordered from S-tier to lower tiers.
   * Returns an array of CosmeticOptions, one for each unique icon shape found.
   */
  private getUniqueMinimapIcons(tierCosmetics: TierCosmetic[], defaultIcon: MinimapIconShape): CosmeticOptions[] {
    // Sort tiers by order (S-tier first, then A, B, C, D, E, F)
    const sortedTiers = [...tierCosmetics].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    const uniqueIcons: MinimapIconShape[] = [];
    const seenShapes = new Set<MinimapIconShape>();

    // Collect unique icons in order from S-tier to lower tiers
    for (const tier of sortedTiers) {
      const iconShape = tier?.displayOptions?.cosmeticOptions?.minimapIconShape;
      if (iconShape && !seenShapes.has(iconShape)) {
        uniqueIcons.push(iconShape);
        seenShapes.add(iconShape);
      }
    }

    // If no icons found, use default
    if (uniqueIcons.length === 0) {
      uniqueIcons.push(defaultIcon);
    }

    // Convert to CosmeticOptions array
    return uniqueIcons.map(iconShape =>
      new CosmeticOptions({
        highlight: true,
        highlightColor: Color.White,
        minimapIconSize: MinimapIconSize.Small,
        minimapIconShape: iconShape
      })
    );
  }

  private getTierColor(tier: string): string {
    const tierCosmetic = this.filter.tierCosmetics.find(t => t.tier === tier);
    return (tierCosmetic?.displayOptions.cosmeticOptions?.highlight && tierCosmetic?.displayOptions.cosmeticOptions?.highlightColor)
      ? tierCosmetic.displayOptions.cosmeticOptions.highlightColor
      : 'Visible';
  }
}
